import type { CountryCatalogItem } from '../../shared/country_field/country_options.js';
import type {
  CheckoutBookingForm,
  CheckoutBudget,
  CheckoutBudgetQuery,
  CheckoutField,
  CheckoutOption,
  CheckoutProductType,
  CheckoutSubmitBlocker,
  CheckoutTravelerDraft,
  CheckoutTripService,
  ConciergeProductType,
  ConciergeReasonOption,
  ConciergeReasonProductType,
  ConciergeRentalOptions,
} from './checkout_source.js';
import type { CheckoutCopy } from './checkout_copy.js';

/*
 * What the checkout organism takes from the host besides the subject (#5000):
 * the IO (the booking form, the budget, the trip's services, the catalogs) and
 * the checkout rules the wire contract owns. The kit never imports the wire, so
 * the host hands its own functions in; the forms, the submit builder and the BFF
 * then all judge by the same rule.
 */

/**
 * The checkout's reads, one shape keyed by product. Only `getBookingForm` and
 * `getBudget` are required; every optional read degrades fail-soft when absent.
 * The host keeps the object referentially stable while its reads are the same:
 * a new identity reloads every traveler's form and drops an open drawer's drafts.
 */
export interface CheckoutIo {
  getBookingForm: (
    productType: CheckoutProductType,
    travelerId?: string,
  ) => Promise<CheckoutBookingForm>;
  getBudget: (
    productType: CheckoutProductType,
    query: CheckoutBudgetQuery,
  ) => Promise<CheckoutBudget>;
  /** The services the block's trip already bought ("Your trip"). Absent, or with
   *  no readable trip, the panel shows only the cart. */
  getTripServices?: ((tripId: string) => Promise<CheckoutTripService[]>) | undefined;
  /** The photo of each traveler by id (pre-signed, short-lived URLs, so never
   *  stored in the block). Absent or failing, the avatar shows the initials. */
  getTravelerPhotos?:
    | ((ids: readonly string[]) => Promise<Readonly<Record<string, string | null>>>)
    | undefined;
  /** Server search of cost centers, scoped by company; absent, the field keeps
   *  the form's embedded catalog. */
  searchCostCenters?: ((companyId: string, term?: string) => Promise<CheckoutOption[]>) | undefined;
  /** The "why ask the team" catalog of the concierge request; absent, the
   *  request goes without that field. */
  getConciergeReasons?:
    | ((productType: ConciergeReasonProductType) => Promise<ConciergeReasonOption[]>)
    | undefined;
  /** The rental concierge catalogs (categories and companies). Without them the
   *  rental request cannot close: the category is mandatory. */
  getRentalConciergeOptions?: (() => Promise<ConciergeRentalOptions>) | undefined;
  /** The nationality and issuing-country catalog, in `locale` (the server
   *  translates the `label`); absent or empty, both fields stay read-only with the
   *  stored value. */
  getCountries?: ((locale: string) => Promise<CountryCatalogItem[]>) | undefined;
}

/** What one traveler's block still lacks, as the wire contract judges it. */
export interface CheckoutBlockersInput {
  fields: CheckoutField[];
  withinPolicy: boolean;
  enableProjectField: boolean;
  /** The traveler's allocation rows (rateio, up to 10). */
  allocations: readonly {
    companyId: string;
    costCenterId: string;
    projectId: string;
    percent?: number | undefined;
  }[];
  /** The `allow_many_allocations` flag: without it, more than one row blocks. */
  allowManyAllocations?: boolean;
  motiveId: string;
  justificationId: string | undefined;
  missingRequired: NonNullable<NonNullable<CheckoutBookingForm['traveler']>['missingRequired']>;
  travelerDraft: CheckoutTravelerDraft;
  /** `false` ⇒ this block does not own the allocation. */
  allocationRequired?: boolean;
  /** `false` ⇒ the justification catalog is empty, so it cannot gate. */
  justificationSelectable?: boolean;
}

/** The checkout rules of the wire contract, injected by the host. */
export interface CheckoutRules {
  /** The manifest of an offer's purchase (no justification within policy). */
  offerFields: (productType: CheckoutProductType, withinPolicy: boolean) => CheckoutField[];
  /** What still blocks one traveler's block, in screen order. */
  submitBlockers: (input: CheckoutBlockersInput) => CheckoutSubmitBlocker[];
  /** The block that owns the allocation when only one pays: the requester when
   *  they travel, else the policy traveler; `undefined` when neither does. */
  allocationOwnerId: (
    travelerIds: readonly string[],
    requesterId: string | undefined,
    policyTravelerId: string | undefined,
  ) => string | undefined;
  /** The hotel rooms, each with its holder first (#5522): the holders are the
   *  blocks that carry an allocation in the `'requester'` mode. Without the
   *  occupancy, one room paid by the owner. */
  hotelRooms: (
    travelerIds: readonly string[],
    ownerId: string | undefined,
    guestsPerRoom: number | undefined,
  ) => string[][];
  /** The manifest of the concierge request (no identity but the bus one's). */
  conciergeFields: (productType: ConciergeProductType) => CheckoutField[];
  /** A typed CPF passes only with the right check digits (digits only). */
  isValidCpf: (digits: string) => boolean;
  /** The CPF as it is typed: "123.456.789-0". */
  maskCpf: (value: string) => string;
}

/** A telemetry event of the organism; the host decides where it goes. */
export type CheckoutTrack = (
  event: string,
  props?: Record<string, string | number | boolean>,
) => void;

/**
 * Everything the checkout organism takes from the host: the reads, the wire
 * rules, the texts and the reader's language, the airport city table and the
 * telemetry. It comes once, by the drawer's `env` prop.
 */
export interface CheckoutEnv {
  /** `null` ⇒ no host reads (preview): the forms fail closed. */
  readonly io: CheckoutIo | null;
  readonly rules: CheckoutRules;
  readonly copy: CheckoutCopy;
  readonly locale: string;
  /** The city of an airport (the domain's table); `undefined` when unknown. */
  readonly cityOf: (iata: string) => string | undefined;
  readonly track: CheckoutTrack;
}

/** Whether the manifest renders the motive or justification select. */
export function hasSelectField(
  fields: readonly CheckoutField[],
  id: Extract<CheckoutField, { kind: 'select' }>['id'],
): boolean {
  return fields.some((field) => field.kind === 'select' && field.id === id);
}
